import { Observable, BehaviorSubject, of as observableOf } from "rxjs";
import { mergeMap, catchError, share } from "rxjs/operators";
import { AuthenticationService } from "../AuthenticationService/authentication.service";
import { EmployeeSideDelegate } from "./employee-side.delegate";
import
{
    IEmployee,
    IEmployeeAddress,
    IUpdateEmployeeResponse,
    IEmpPayroll,
    IEmpTaxDoc,
    INewEmployeeAddress,
    INewEmployeeDocument,
    INewEmploymentHistory,
    INewEmployeeAbsence,
    IEmployeeAbsence
} from "./employee-side.interface";

/**
 * EmployeeSideService � facade for the employee self-service domain.
 *
 * Distinct from the admin-facing EmployeeService:
 *   - All reads are scoped to the currently authenticated employee
 *     (employeeId is read from AuthenticationService, never passed in).
 *   - Surfaces the streams used by the employee portal: own profile,
 *     own paystubs, own tax docs, own absences.
 *
 * Construction is handled by EmployeeSideServiceFactory and access by
 * ServiceLocator.getEmployeeSideService().
 */
export class EmployeeSideService
{
    private employeeData: IEmployee = null;
    private employeeSubject = new BehaviorSubject<IEmployee>(null);
    public employee: Observable<IEmployee> = this.employeeSubject.asObservable();

    private employeePaystubsData: IEmpPayroll[] = null;
    private employeePaystubsSubject = new BehaviorSubject<IEmpPayroll[]>(null);
    public employeePaystubs: Observable<IEmpPayroll[]> = this.employeePaystubsSubject.asObservable();

    private employeeTaxDocsData: IEmpTaxDoc[] = null;
    private employeeTaxDocsSubject = new BehaviorSubject<IEmpTaxDoc[]>(null);
    public employeeTaxDocs: Observable<IEmpTaxDoc[]> = this.employeeTaxDocsSubject.asObservable();

    private employeeAbsenceData: IEmployeeAbsence[] = null;
    private employeeAbsenceSubject = new BehaviorSubject<IEmployeeAbsence[]>(null);
    public employeeAbsence: Observable<IEmployeeAbsence[]> = this.employeeAbsenceSubject.asObservable();

    constructor(
        private delegate: EmployeeSideDelegate,
        private authenticationService: AuthenticationService
    ) { }

    /** Fetches the logged-in employee's profile (cached). */
    public getEmployee(): Observable<boolean>
    {
        if (this.employeeData !== null) { return observableOf(true); }

        const employeeId = this.authenticationService.GetEmployeeId();
        const response = this.delegate.getEmployeeByEmpId(employeeId);

        response.subscribe(
            (employee: IEmployee) =>
            {
                this.employeeData = employee;
                this.employeeSubject.next(this.employeeData);
            },
            () => { /* swallow � observable below maps to false */ }
        );

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    /** Toggle paperless preference for the current employee. */
    public updateEmployeePaperless(paperless: boolean): Observable<boolean>
    {
        const employeeId = this.authenticationService.GetEmployeeId();
        const response = this.delegate.updateEmployeePaperless(employeeId, paperless);

        response.subscribe(
            (employee: IEmployee) =>
            {
                this.employeeData = employee;
                this.employeeSubject.next(this.employeeData);
            },
            () => { /* noop */ }
        );

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    /** Loads the employee's paystubs for the given year (defaults to current). */
    public getEmployeePayroll(year: number = new Date().getFullYear()): Observable<boolean>
    {
        const employeeId = this.authenticationService.GetEmployeeId();
        const response = this.delegate.getEmployeePaystubsByEmpId(employeeId, year);

        response.subscribe(
            (paystubs: IEmpPayroll[]) =>
            {
                this.employeePaystubsData = paystubs;
                this.employeePaystubsSubject.next(this.employeePaystubsData);
            },
            () => { /* noop */ }
        );

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    /** Loads the employee's tax documents for the given year (defaults to current). */
    public getEmployeeTaxDocs(year: number = new Date().getFullYear()): Observable<boolean>
    {
        const employeeId = this.authenticationService.GetEmployeeId();
        const response = this.delegate.getEmployeeTaxDocsByEmpId(employeeId, year);

        response.subscribe(
            (taxDocs: IEmpTaxDoc[]) =>
            {
                this.employeeTaxDocsData = taxDocs;
                this.employeeTaxDocsSubject.next(this.employeeTaxDocsData);
            },
            () => { /* noop */ }
        );

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    /** Returns the currently cached employee object (null if not yet loaded). */
    public getEmployeeById(): IEmployee
    {
        return this.employeeData;
    }

    public createEmployeeAbsence(employeeAbsence: INewEmployeeAbsence): Observable<boolean>
    {
        employeeAbsence.employeeId = this.authenticationService.GetEmployeeId();
        const payload = { ...employeeAbsence, ...this.authenticationService.getBaseRequest } as INewEmployeeAbsence;
        const response = this.delegate.createEmployeeAbsence(payload);

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    /** Look up a single absence in the cached set by tracking id. */
    public getEmployeeAbsenceByTrackingId(employeeAbsenceTrackingId: string): IEmployeeAbsence | undefined
    {
        if (!this.employeeAbsenceData) { return undefined; }
        return this.employeeAbsenceData.find(a => (a as any).employeeAbsenceTrackingId === employeeAbsenceTrackingId);
    }

    public updateEmployeeAbsence(employeeAbsence: IEmployeeAbsence): Observable<boolean>
    {
        employeeAbsence.employeeId = this.authenticationService.GetEmployeeId();
        // Same endpoint handles upsert in the backend; mirrors original behavior.
        const payload = { ...employeeAbsence, ...this.authenticationService.getBaseRequest } as INewEmployeeAbsence;
        const response = this.delegate.createEmployeeAbsence(payload);

        return response.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }

    public createNewAddress(employeeAddress: IEmployeeAddress, employeeId: string): Observable<string>
    {
        const newAddress: INewEmployeeAddress = { ...employeeAddress, employeeId };
        const response = this.delegate.createEmployeeAddress(newAddress);

        return response.pipe(
            mergeMap((r: IUpdateEmployeeResponse) => observableOf(r ? r.id : null)),
            catchError(() => observableOf(null)),
            share());
    }

    public createNewEmployeeDocuments(employeeDocuments: INewEmployeeDocument): Observable<boolean>
    {
        return this.processBoolean(this.delegate.createEmployeeDocuments(employeeDocuments));
    }

    public createNewEmployeeHistory(employeeHistory: INewEmploymentHistory): Observable<boolean>
    {
        return this.processBoolean(this.delegate.createEmployeeHistory(employeeHistory));
    }

    private processBoolean(source: Observable<unknown>): Observable<boolean>
    {
        return source.pipe(
            mergeMap((r: any) => observableOf(!!r)),
            catchError(() => observableOf(false)),
            share());
    }
}
